<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import config from '@/config/ConfigProvider';
import { useLocale, useLocalisation } from '@/plugins/translation';
import useGuiStore from '@/store/gui';
import { useRoute } from 'vue-router';
import { configHelper } from '@ilteducation/config';
import { useClientAnalytics } from '@ilteducation/client-analytics';

type Props = {
  searchLabel?: string;
};

defineProps<Props>();

const { t, l } = useLocale();
const { language } = useLocalisation();
const serviceName = ref<string>(config.value('serviceName'));
const logoImageFile = ref<string>(config.value('logoImageFile'));
const showGuides = ref<string>(config.value('showGuides'));
const showMaterials = ref<string>(config.value('showMaterials'));
const showGames = ref<string>(config.value('showGames'));
const countryCode = ref<string>(config.value('countryCode'));
const { toggleBurgerMenu, closeBurgerMenu } = useGuiStore();
const { getAppUrl } = configHelper;
const articlesUrl = getAppUrl('artiklar.begreppa');

const capitalizationClass = computed(() => (language.value === 'en-GB' ? 'text-capitalize' : 'text-capitalize-first'));
const logoImageUrl = computed(() => new URL(`/src/assets/images/${logoImageFile.value}`, import.meta.url).href);
const { sendAnalyticsEvent } = useClientAnalytics();

const tabs = [
  { path: '/videos', icon: 'fa-camera-movie', label: l.tabs.films, disabled: false },
  { path: '/guides', icon: 'fa-book', label: l.tabs.guides, disabled: !showGuides.value },
  { path: '/materials', icon: 'fa-file-lines', label: l.tabs.materials, disabled: !showMaterials.value },
  { path: '/games', icon: 'fa-gamepad-modern', label: l.tabs.games, disabled: !showGames.value },
  {
    href: articlesUrl,
    icon: 'fa-newspaper',
    label: l.tabs.articles,
    disabled: countryCode.value !== 'se',
    clickHandler: () =>
      sendAnalyticsEvent('AppUserEventType', {
        eventName: 'PageNavigation',
        metadata: {
          to: `${articlesUrl}`,
          from: window.location.pathname,
        },
      }),
  },
];
const activeTab = ref<number>(0);
const route = useRoute();
watch(
  route,
  (newRoute) => {
    const tab = tabs.findIndex((t) => t.path && newRoute.path.startsWith(t.path));
    if (tab !== -1) {
      activeTab.value = tab;
    }
  },
  { immediate: true },
);

const skipToContent = () => document.querySelector('main')?.focus();
</script>

<template>
  <v-app-bar color="surface-container-lowest" class="px-1 px-lg-2">
    <v-btn class="d-sr-only-focusable" color="primary" @click="skipToContent">{{ t(l.button_skip_to_content) }}</v-btn>
    <v-row no-gutters style="max-width: none">
      <v-col cols="12" md="3" lg="2" class="d-flex align-center">
        <div class="d-flex align-center justify-start">
          <v-app-bar-nav-icon
            class="nav-button"
            :aria-label="t(l.label_burger_menu_button)"
            @click="toggleBurgerMenu"
            @keydown.esc="closeBurgerMenu"
          />
          <v-app-bar-title class="toolbar-title pl-1 pl-sm-2">
            <router-link :to="{ name: 'begreppaLanding' }">
              <v-img
                :src="logoImageUrl"
                height="100%"
                max-height="30"
                width="140"
                cover
                :alt="`${serviceName} ${t(l.home)}`"
              />
            </router-link>
          </v-app-bar-title>
        </div>
      </v-col>
      <v-col cols="7" md="9" lg="8">
        <nav :aria-label="t(l.label_main_navigation)" v-if="countryCode === 'se'" class="d-none d-md-block">
          <v-list
            class="justify-lg-center d-flex flex-row toolbar-links gc-2 gc-lg-3"
            variant="text"
            bg-color="transparent"
            tag="ul"
            :aria-label="t(l.label_main_navigation)"
            role="list"
          >
            <li v-for="tab in tabs" :key="tab.path || tab.href" v-show="!tab.disabled">
              <v-list-item
                :rounded="true"
                :href="tab.href"
                :to="tab.path"
                class="h-100 px-2 px-lg-3"
                active-class="active-nav-link"
                @click="tab.clickHandler"
              >
                <template #default="{ isActive }">
                  <div class="d-flex px-1">
                    <v-icon class="mr-2" size="small" :icon="`${isActive ? 'fa:fas' : 'fa:fat'} ${tab.icon}`"></v-icon>
                    <v-list-item-title :class="capitalizationClass" class="tab-text">
                      {{ t(tab.label) }}</v-list-item-title
                    >
                  </div>
                </template>
              </v-list-item>
            </li>
          </v-list>
        </nav>
      </v-col>
    </v-row>
  </v-app-bar>
</template>

<style lang="scss" scoped>
@use '@/foundations/theme/settings';

:deep(.toolbar-links) {
  .v-list-item {
    overflow: visible !important;

    .v-list-item__overlay,
    .v-list-item__content,
    .v-list-item__underlay {
      overflow: visible !important;
    }
  }
  a {
    color: rgb(var(--v-theme-on-surface-medium)) !important;
    &:hover {
      background-color: rgba(0, 0, 0, 0.04);
      color: rgb(var(--v-theme-primary)) !important;
    }
  }
}
a.active-nav-link {
  color: rgba(var(--v-theme-primary)) !important;
}

.tab-text {
  font-size: 0.875rem; /* v-tab uses this */
  font-weight: 500;
  line-height: 1.25rem;
  letter-spacing: 0.0892857143em;
}

.nav-button {
  color: rgba(var(--v-theme-secondary)) !important;
  transition:
    0.3s cubic-bezier(0.25, 0.8, 0.5, 1),
    visibility 0s;

  .v-icon {
    transition: none;
  }

  &:hover {
    color: white !important;
    background: rgb(var(--v-theme-brand));
  }

  &:focus {
    outline: none !important;
  }
}

.text-capitalize-first {
  text-transform: lowercase;
}

.text-capitalize-first::first-letter {
  text-transform: uppercase;
}
</style>
